import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useAdminStatus } from './useAdminStatus';

export const useApprovalStatus = () => {
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, loading: adminLoading } = useAdminStatus();
  const [approvalStatus, setApprovalStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkStatus = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      // If admin, always approved
      if (isAdmin) {
        setApprovalStatus('approved');
        setLoading(false);
        return;
      }

      try {
        // Check profile approval status
        const { data: profileData, error } = await supabase
          .from('profiles')
          .select('approval_status')
          .eq('id', user.id)
          .single();

        if (error) {
          // Log only in development to prevent info leakage
          if (import.meta.env.DEV) {
            console.error('Error checking approval status:', error);
          }
          setApprovalStatus('pending');
        } else {
          setApprovalStatus(profileData?.approval_status || 'pending');
        }
      } catch (error) {
        // Log only in development to prevent info leakage
        if (import.meta.env.DEV) {
          console.error('Error in approval check:', error);
        }
        setApprovalStatus('pending');
      } finally {
        setLoading(false);
      }
    };

    if (!authLoading && !adminLoading) {
      checkStatus();
    }
  }, [user, authLoading, isAdmin, adminLoading]);

  return {
    approvalStatus,
    isApproved: approvalStatus === 'approved' || isAdmin,
    isPending: approvalStatus === 'pending' && !isAdmin,
    isRejected: approvalStatus === 'rejected' && !isAdmin,
    isAdmin,
    loading: authLoading || adminLoading || loading,
  };
};
